/**
 * The voice a caller hears, as a list on its own pane. OUR TEAM ONLY.
 *
 * INLINE NOW, WHERE IT WAS A DIALOG. `VoicePicker` put forty rows, two filters and an audio
 * transport in a dialog because unfolding them inside a settings section would push
 * everything below a screen and a half down. A pane has nothing below it, so the list is the
 * pane, as Settings v2 draws it: two filters, then rows of play, name, gender, accent and
 * "Use this voice".
 *
 * CHOOSING IS A DRAFT; SAVING RE-RECORDS THE GREETING. "Use this voice" marks the row and
 * the page's bar does the rest - `POST /v1/settings/voice`, which records the greeting again
 * in the new voice in the same request. If that recording fails the voice is still saved, and
 * the bar says so afterwards rather than this pane going quiet about it.
 *
 * NOBODY IS TOLD WHO THE VENDOR IS - no provider, model or voice id in anything rendered, and
 * the clips come through the Worker. ONE CLIP AT A TIME, because two voices talking over each
 * other make the list useless for the one thing it is for. Both rules are `VoicePicker`'s,
 * kept.
 */

import { useState } from "react";
import { useApi, type VoiceBlock, type VoiceCatalogue } from "../../../api";
import { ClipSquare } from "../../RecordingPlayer";
import { Button, Segmented, SettingCard, cx } from "../../../ui";
import { useDraft } from "../draft";

const ANY = "any";

const sentence = (word: string | null) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : "");

export function VoicePane({ agentId, voice }: { agentId: string; voice: VoiceBlock | null }) {
  const { state, set } = useDraft();
  const { data, loading, error } = useApi<VoiceCatalogue>(`/v1/settings/voices?agent=${encodeURIComponent(agentId)}`);
  const [gender, setGender] = useState(ANY);
  const [accent, setAccent] = useState(ANY);
  const [playing, setPlaying] = useState<string | null>(null);

  const saved = state.baseline.voice.id;
  const chosen = state.draft.voice.id;

  if (loading) return <p className="text-sm text-ink-faint">Loading the voices…</p>;
  if (error || !data?.available) {
    return (
      <p className="text-sm text-ink-faint">
        Voices are temporarily unavailable, so there is nothing to choose from right now. The current voice is unaffected. Try
        again in a few minutes.
      </p>
    );
  }

  const rows = data.voices.filter((v) => (gender === ANY || v.gender === gender) && (accent === ANY || v.accent === accent));
  // Suggested first: tagged for phone lines, or sounding like this business's own callers.
  const ordered = [...rows.filter((v) => v.suggested), ...rows.filter((v) => !v.suggested)];
  // THE VOICE IN USE, WHEN THE CATALOGUE NO LONGER HAS IT: pinned from the snapshot, with no
  // Play, so the pane never looks as if nothing is in use today.
  const missing = voice && !data.voices.some((v) => v.id === voice.id);

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <Segmented
          label="Voice"
          value={gender}
          onChange={setGender}
          options={[{ value: ANY, label: "Any voice" }, ...data.genders.map((value) => ({ value, label: sentence(value) }))]}
        />
        <Segmented
          label="Accent"
          value={accent}
          onChange={setAccent}
          options={[{ value: ANY, label: "Any accent" }, ...data.accents.map((value) => ({ value, label: value }))]}
        />
      </div>

      <SettingCard flush>
        {missing && voice && (
          <Row
            name={voice.name ?? "The voice you are using"}
            gender={sentence(voice.gender)}
            accent={voice.accent ?? ""}
            state={chosen === voice.id ? (saved === voice.id ? "in use" : "chosen") : "choose"}
            onChoose={() => set("voice", { id: voice.id })}
          />
        )}
        {ordered.map((v, index) => (
          <Row
            key={v.id}
            first={index === 0 && !missing}
            name={v.name}
            gender={sentence(v.gender)}
            accent={v.accent ?? ""}
            suggested={v.suggested}
            state={chosen === v.id ? (saved === v.id ? "in use" : "chosen") : "choose"}
            onChoose={() => set("voice", { id: v.id })}
            clip={
              v.preview ? (
                <ClipSquare
                  path={`/v1/settings/voices/preview?agent=${encodeURIComponent(agentId)}&id=${v.id}`}
                  label={`Play a sample of ${v.name}`}
                  active={playing === v.id}
                  onPlay={() => setPlaying(v.id)}
                />
              ) : (
                <span className="size-8.5" />
              )
            }
          />
        ))}
        {ordered.length === 0 && !missing && <p className="px-5 py-6 text-sm text-ink-faint">No voices match those filters.</p>}
      </SettingCard>
      <span className="text-xs text-ink-faint">Changing the voice records the greeting again in the new voice.</span>
    </>
  );
}

function Row({
  name,
  gender,
  accent,
  suggested,
  state,
  onChoose,
  clip,
  first,
}: {
  name: string;
  gender: string;
  accent: string;
  suggested?: boolean;
  state: "in use" | "chosen" | "choose";
  onChoose: () => void;
  clip?: React.ReactNode;
  first?: boolean;
}) {
  return (
    <div
      className={cx(
        "grid grid-cols-[40px_minmax(0,1fr)_110px_110px_150px] items-center gap-3.5 px-5 py-3",
        !first && "border-t border-line-soft",
        state !== "choose" && "bg-accent-soft/50",
        "max-[720px]:grid-cols-[40px_minmax(0,1fr)_auto]",
      )}
    >
      {clip ?? <span />}
      <span className="flex min-w-0 items-center gap-2 text-base font-medium text-ink">
        <span className="truncate">{name}</span>
        {suggested && <span className="text-2xs font-normal text-ink-faint">Suggested</span>}
      </span>
      <span className="text-xs text-ink-soft max-[720px]:hidden">{gender}</span>
      <span className="text-xs text-ink-soft max-[720px]:hidden">{accent}</span>
      <span className="justify-self-end">
        {state === "in use" && <span className="text-xs font-semibold text-accent">In use</span>}
        {state === "chosen" && <span className="text-xs font-semibold text-accent">Chosen, saves with the rest</span>}
        {state === "choose" && (
          <Button type="button" className="h-8 px-3 text-xs" onClick={onChoose}>
            Use this voice
          </Button>
        )}
      </span>
    </div>
  );
}
